import { supabase } from "@/integrations/supabase/client";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface CrmNotification {
  id: string;
  user_id: string;
  type: "new_lead" | "visit_scheduled" | "lead_hot" | "task_overdue" | "daily_summary" | string;
  title: string;
  message: string | null;
  lead_id: string | null;
  task_id: string | null;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
}

export interface CrmLead {
  id: string;
  client_name: string;
  client_phone: string | null;
  client_whatsapp: string | null;
  client_email: string | null;
  cpf: string | null;
  rg: string | null;
  birth_date: string | null;
  marital_status: string | null;
  profession: string | null;
  company: string | null;
  income: number | null;
  city: string | null;
  state: string | null;
  address: string | null;
  source: string | null;
  campaign: string | null;
  ad_group: string | null;
  ad_name: string | null;
  keyword: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  landing_page: string | null;
  goal: string | null;
  client_type: string | null;
  interests: string[];
  price_range: string | null;
  regions: string[];
  bedrooms: string | null;
  garage: string | null;
  stage: string;
  score: number;
  classification: string;
  broker: string | null;
  deal_value: number | null;
  closed_at: string | null;
  lost_reason: string | null;
  last_contact_at: string | null;
  property_name: string | null;
  neighborhood: string | null;
  temperature: "frio" | "morno" | "quente";
  status: string;
  visit_date: string | null;
  general_notes: string | null;
  follow_up_at: string | null;
  follow_up_notes: string | null;
  follow_up_done: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface CrmEvent {
  id: string;
  lead_id: string;
  event_type: string;
  title: string;
  description: string | null;
  actor: string | null;
  created_at: string;
}

export interface CrmTask {
  id: string;
  lead_id: string;
  title: string;
  description: string | null;
  due_at: string;
  priority: string;
  assignee: string | null;
  task_type: string;
  is_done: boolean;
  done_at: string | null;
}

export interface CrmLeadListing {
  id: string;
  lead_id: string;
  listing_id: string | null;
  listing_title: string | null;
  relation: string;
  notes: string | null;
  created_at: string;
}

export interface CrmDocument {
  id: string;
  lead_id: string;
  category: string;
  file_name: string;
  file_url: string;
  created_at: string;
}

/* ------------------------------------------------------------------ */
/* Catalogs                                                            */
/* ------------------------------------------------------------------ */

export const STAGES: { value: string; label: string }[] = [
  { value: "novo_lead", label: "Novo Lead" },
  { value: "primeiro_contato", label: "Primeiro Contato" },
  { value: "qualificacao", label: "Qualificação" },
  { value: "imoveis_enviados", label: "Imóveis Enviados" },
  { value: "cliente_interessado", label: "Cliente Interessado" },
  { value: "visita_agendada", label: "Visita Agendada" },
  { value: "visita_realizada", label: "Visita Realizada" },
  { value: "negociacao", label: "Negociação" },
  { value: "proposta", label: "Proposta" },
  { value: "contra_proposta", label: "Contra Proposta" },
  { value: "financiamento", label: "Financiamento" },
  { value: "documentacao", label: "Documentação" },
  { value: "contrato", label: "Contrato" },
  { value: "escritura", label: "Escritura" },
  { value: "pos_venda", label: "Pós Venda" },
  { value: "cliente_fidelizado", label: "Cliente Fidelizado" },
  { value: "indicacao", label: "Indicação" },
  { value: "perdido", label: "Perdido" },
];

export const stageLabel = (v: string) => STAGES.find((s) => s.value === v)?.label || v;

export const SOURCES = [
  "Google Ads", "Meta Ads", "Instagram", "Facebook", "Site", "Landing Page",
  "WhatsApp", "Indicação", "Parceiro", "Orgânico", "Portal Imobiliário", "Outro",
];

export const GOALS = ["Comprar", "Investir", "Airbnb", "Morar", "Troca", "Locação", "Revenda"];

export const CLIENT_TYPES = [
  "Pessoa Física", "Pessoa Jurídica", "Estrangeiro", "Investidor", "Casal", "Família", "Aposentado",
];

export const INTERESTS = [
  "Apartamento", "Cobertura", "Studio", "Garden", "Casa", "Terreno", "Lançamento", "Pronto",
];

export const PRICE_RANGES = [
  "Até 500 mil", "500 mil a 1 milhão", "1 a 2 milhões",
  "2 a 5 milhões", "5 a 10 milhões", "Acima de 10 milhões",
];

export const REGIONS = [
  "Copacabana", "Ipanema", "Leblon", "Lagoa", "Botafogo", "Humaitá", "Gávea",
  "Jardim Botânico", "Flamengo", "Laranjeiras", "Catete", "Centro",
  "Barra da Tijuca", "Recreio", "Outro",
];

export const BEDROOM_OPTIONS = ["Studio", "1", "2", "3", "4", "5+"];
export const GARAGE_OPTIONS = ["Sem vaga", "1 vaga", "2 vagas", "3+"];
export const MARITAL_OPTIONS = ["Solteiro(a)", "Casado(a)", "União estável", "Divorciado(a)", "Viúvo(a)"];

export const PRIORITIES = [
  { value: "alta", label: "Alta" },
  { value: "media", label: "Média" },
  { value: "baixa", label: "Baixa" },
];

export const TASK_TYPES = [
  { value: "follow_up", label: "Follow up" },
  { value: "ligacao", label: "Ligação" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "E-mail" },
  { value: "visita", label: "Visita" },
  { value: "reuniao", label: "Reunião" },
  { value: "documentacao", label: "Documentação" },
  { value: "escritura", label: "Escritura" },
];

export const EVENT_TYPES = [
  { value: "nota", label: "Anotação" },
  { value: "whatsapp_enviado", label: "WhatsApp enviado" },
  { value: "whatsapp_recebido", label: "WhatsApp recebido" },
  { value: "email_enviado", label: "E-mail enviado" },
  { value: "email_recebido", label: "E-mail recebido" },
  { value: "ligacao", label: "Ligação" },
  { value: "visita", label: "Visita" },
  { value: "proposta", label: "Proposta" },
  { value: "contrato", label: "Contrato" },
  { value: "documentacao", label: "Documentação" },
  { value: "escritura", label: "Escritura" },
  { value: "status", label: "Alteração de status" },
  { value: "arquivo", label: "Arquivo" },
];

export const DOC_CATEGORIES = [
  "CPF", "RG", "Comprovante", "Contrato", "Proposta", "Escritura", "Matrícula", "Fotos", "PDF", "Outro",
];

export const LISTING_RELATIONS = [
  { value: "visualizado", label: "Visualizado" },
  { value: "enviado", label: "Enviado" },
  { value: "favorito", label: "Favorito" },
  { value: "visitado", label: "Visitado" },
  { value: "negociado", label: "Negociado" },
  { value: "comprado", label: "Comprado" },
];

export const WON_STAGES = ["escritura", "pos_venda", "cliente_fidelizado", "indicacao"];

/* ------------------------------------------------------------------ */
/* Scoring                                                             */
/* ------------------------------------------------------------------ */

const EVENT_POINTS: Record<string, number> = {
  lead_criado: 20,
  whatsapp_recebido: 15,
  email_recebido: 10,
  ligacao: 10,
  visita: 20,
  proposta: 40,
  contrato: 40,
};

/** Lead score 0–100 derived from the timeline and inactivity. */
export function computeScore(lead: CrmLead, events: CrmEvent[]): number {
  if (lead.stage === "perdido") return 0;
  let score = 0;
  events.forEach((e) => {
    score += EVENT_POINTS[e.event_type] ?? 0;
  });
  if (lead.stage === "visita_agendada") score += 30;
  if (WON_STAGES.includes(lead.stage)) score += 30;
  const days = daysSince(lead.last_contact_at || lead.created_at);
  if (days > 30) score -= 50;
  else if (days > 7) score -= 20;
  return Math.max(0, Math.min(100, score));
}

export function classify(score: number, stage?: string): string {
  if (stage === "perdido") return "perdido";
  if (score >= 85) return "muito_quente";
  if (score >= 60) return "quente";
  if (score >= 35) return "morno";
  return "frio";
}

export const CLASSIFICATIONS: Record<string, { label: string; className: string }> = {
  muito_quente: { label: "Muito Quente", className: "bg-red-600 text-white border-red-700" },
  quente: { label: "Quente", className: "bg-orange-100 text-orange-900 border-orange-300" },
  morno: { label: "Morno", className: "bg-amber-100 text-amber-900 border-amber-300" },
  frio: { label: "Frio", className: "bg-sky-100 text-sky-900 border-sky-300" },
  perdido: { label: "Perdido", className: "bg-muted text-muted-foreground border-border" },
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

export function daysSince(iso: string | null): number {
  if (!iso) return 999;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

export const isSameDay = (iso: string | null, ref = new Date()) => {
  if (!iso) return false;
  const d = new Date(iso);
  return d.toDateString() === ref.toDateString();
};

export const brl = (v: number | null | undefined) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })
    : "—";

export const dateTimeBR = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

export const dateBR = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

export function whatsappLink(lead: CrmLead, message?: string) {
  let digits = (lead.client_whatsapp || lead.client_phone || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length <= 11) digits = `55${digits}`;
  const msg =
    message ||
    `Olá ${lead.client_name.split(" ")[0]}, aqui é da TopClass Imóveis Premium. Podemos falar agora?`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
}

export function emailLink(lead: CrmLead) {
  if (!lead.client_email) return null;
  const subject = `TopClass Imóveis Premium — ${lead.property_name || "seu contato"}`;
  const body = `Olá ${lead.client_name.split(" ")[0]},\n\nTudo bem? Sou da TopClass Imóveis Premium.\n\nAtenciosamente,\nTopClass Imóveis Premium\nWhatsApp: (21) 97354-8387`;
  return `mailto:${lead.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/* ------------------------------------------------------------------ */
/* Data access                                                         */
/* ------------------------------------------------------------------ */

const db = supabase as any;

export async function fetchLeads(): Promise<CrmLead[]> {
  const { data } = await db.from("crm_leads").select("*").order("updated_at", { ascending: false });
  return (data || []) as CrmLead[];
}

export async function fetchTasks(): Promise<CrmTask[]> {
  const { data } = await db.from("crm_lead_tasks").select("*").order("due_at", { ascending: true });
  return (data || []) as CrmTask[];
}

export async function fetchEvents(leadId: string): Promise<CrmEvent[]> {
  const { data } = await db
    .from("crm_lead_events")
    .select("*")
    .eq("lead_id", leadId)
    .order("created_at", { ascending: false });
  return (data || []) as CrmEvent[];
}

export async function logEvent(
  leadId: string,
  event_type: string,
  title: string,
  description?: string,
) {
  await db.from("crm_lead_events").insert({
    lead_id: leadId,
    event_type,
    title,
    description: description || null,
    actor: "Admin",
  });
}

export async function updateLead(id: string, patch: Record<string, unknown>) {
  return await db.from("crm_leads").update(patch).eq("id", id);
}

/** Automation: creates the standard follow-up cadence for a new lead. */
export async function createFollowUpSequence(leadId: string) {
  const steps: { days: number; title: string; priority: string }[] = [
    { days: 0, title: "Primeiro contato", priority: "alta" },
    { days: 1, title: "Retorno em 1 dia", priority: "alta" },
    { days: 3, title: "Retorno em 3 dias", priority: "media" },
    { days: 7, title: "Retorno em 7 dias", priority: "media" },
    { days: 15, title: "Retorno em 15 dias", priority: "baixa" },
    { days: 30, title: "Retorno em 30 dias", priority: "baixa" },
  ];
  const rows = steps.map((s) => ({
    lead_id: leadId,
    title: s.title,
    priority: s.priority,
    task_type: "follow_up",
    due_at: new Date(Date.now() + s.days * 86400000).toISOString(),
  }));
  await db.from("crm_lead_tasks").insert(rows);
}

/** Automation: when the client answers, cancel the remaining scheduled cadence. */
export async function cancelFutureFollowUps(leadId: string) {
  await db
    .from("crm_lead_tasks")
    .delete()
    .eq("lead_id", leadId)
    .eq("is_done", false)
    .eq("task_type", "follow_up")
    .gt("due_at", new Date().toISOString());
}

/* ------------------------------------------------------------------ */
/* Fase 2 — Automations                                                */
/* ------------------------------------------------------------------ */

/** Next-step cadence per pipeline stage: task title + days from now. */
const STAGE_CADENCE: Record<string, { days: number; title: string; type: string; priority: string }[]> = {
  novo_lead: [{ days: 0, title: "Primeiro contato", type: "whatsapp", priority: "alta" }],
  primeiro_contato: [{ days: 1, title: "Qualificar perfil do cliente", type: "whatsapp", priority: "alta" }],
  qualificacao: [{ days: 1, title: "Enviar seleção de imóveis", type: "whatsapp", priority: "alta" }],
  imoveis_enviados: [{ days: 2, title: "Confirmar retorno sobre os imóveis", type: "follow_up", priority: "alta" }],
  cliente_interessado: [{ days: 1, title: "Convidar para visita", type: "whatsapp", priority: "alta" }],
  visita_agendada: [{ days: 0, title: "Confirmar visita com o cliente", type: "whatsapp", priority: "alta" }],
  visita_realizada: [{ days: 1, title: "Colher feedback da visita", type: "follow_up", priority: "alta" }],
  negociacao: [{ days: 1, title: "Retornar com condições ao cliente", type: "ligacao", priority: "alta" }],
  proposta: [{ days: 1, title: "Acompanhar resposta da proposta", type: "follow_up", priority: "alta" }],
  contra_proposta: [{ days: 1, title: "Fechar valor com as partes", type: "ligacao", priority: "alta" }],
  financiamento: [{ days: 3, title: "Acompanhar análise do financiamento", type: "documentacao", priority: "media" }],
  documentacao: [{ days: 2, title: "Cobrar documentos pendentes", type: "documentacao", priority: "alta" }],
  contrato: [{ days: 3, title: "Acompanhar assinatura do contrato", type: "documentacao", priority: "alta" }],
  escritura: [{ days: 7, title: "Acompanhar escritura e RGI", type: "escritura", priority: "media" }],
  pos_venda: [{ days: 30, title: "Contato de pós-venda", type: "follow_up", priority: "baixa" }],
  cliente_fidelizado: [{ days: 90, title: "Manter relacionamento", type: "follow_up", priority: "baixa" }],
  indicacao: [{ days: 7, title: "Pedir indicação", type: "whatsapp", priority: "baixa" }],
};

/**
 * Automation: guarantees the lead always has an open next task.
 * Creates the stage cadence only when nothing is pending, so it never duplicates.
 */
export async function ensureNextTask(leadId: string, stage: string): Promise<boolean> {
  if (stage === "perdido") return false;
  const { data } = await db
    .from("crm_lead_tasks")
    .select("id")
    .eq("lead_id", leadId)
    .eq("is_done", false)
    .limit(1);
  if ((data || []).length > 0) return false;

  const steps = STAGE_CADENCE[stage] || [
    { days: 1, title: "Retomar contato", type: "follow_up", priority: "media" },
  ];
  await db.from("crm_lead_tasks").insert(
    steps.map((s) => ({
      lead_id: leadId,
      title: s.title,
      task_type: s.type,
      priority: s.priority,
      due_at: new Date(Date.now() + s.days * 86400000).toISOString(),
    })),
  );
  return true;
}

/** Automation: runs when a lead moves to a new stage. */
export async function onStageChanged(leadId: string, stage: string) {
  await ensureNextTask(leadId, stage);
  if (stage === "perdido") await cancelFutureFollowUps(leadId);
}

/** Automation batch: fills the gap for every lead that has no open task. */
export async function runCadenceAutomation(leads: CrmLead[]): Promise<number> {
  let created = 0;
  for (const lead of leads) {
    if (await ensureNextTask(lead.id, lead.stage)) created += 1;
  }
  return created;
}

/* ------------------------------------------------------------------ */
/* Notifications                                                      */
/* ------------------------------------------------------------------ */

export async function fetchNotifications(): Promise<CrmNotification[]> {
  const { data } = await db
    .from("crm_notifications")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(100);
  return (data || []) as CrmNotification[];
}

export async function markNotificationRead(id: string) {
  return await db
    .from("crm_notifications")
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq("id", id);
}

export async function markAllNotificationsRead() {
  return await db
    .from("crm_notifications")
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq("is_read", false);
}

export async function deleteNotification(id: string) {
  return await db.from("crm_notifications").delete().eq("id", id);
}

